import { useCallback, useEffect, useState } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";

import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../Form/controls/FormTextField.jsx";
import FormEmailInput from "../Form/controls/FormEmailInput.jsx";
import FormDatePicker from "../Form/controls/FormDatePicker.jsx";
import FormFilePickerField from "../Form/controls/FilePicker/FormFilePickerField.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { createInsurerContactFromClaim } from "@accredo/fe-shared/services/insurerContactService.js";
import NewInsurerContactDialog from "./NewInsurerContactDialog.jsx";
import { useSessionFiles } from "../Form/controls/FilePicker/useSessionFiles.js";
import {
  useClaimAttachmentFiles,
  uploadClaimAttachment,
} from "../Form/controls/FilePicker/useClaimAttachmentFiles.js";
import { fedfKommunikation } from "formdefs/kommunikation.js";
import { colorsFoundation, typographyTokens } from "@accredo/fe-shared/theme/index.js";
import Alert from "@mui/material/Alert";
import { enqueueSnackbar, useSnackbar } from "notistack";

// ---------------------------------------------------------------------------
// Template button definitions (extracted from legacy formdef)
// ---------------------------------------------------------------------------
const FILL_TEMPLATES = fedfKommunikation.formdefinition
  .filter((item) => item.control === "fillbutton")
  .map((item) => ({
    label: item.label,
    actions: item.actions,
    // null = always visible, true = visible when an_versicherer, false = visible when !an_versicherer
    visibleWhenAnVersicherer:
      item.hidden === "isEqualOther('an_versicherer', true)"
        ? false
        : item.hidden === "isEqualOther('an_versicherer', false)"
          ? true
          : null,
  }));

// for "due on" buttons
const QUICK_DATES = [
  { label: "in 1 Woche", days: 7 },
  { label: "in 2 Wochen", days: 14 },
  { label: "in 1 Monat", days: 30 },
  // { label: "nicht erinnern", days: null },
];

const getDateInFuture = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);

  return date;
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (mb >= 1) return `${mb.toFixed(1).replace(".", ",")} MB`;
  return `${Math.round(kb).toLocaleString("de-DE")} KB`;
};

// Outgoing emails are MIME messages: binary parts (the inline logo below,
// and every attachment) are base64-encoded for transport, which inflates
// their raw byte count by ~4/3 on the wire. The rendered HTML layout is
// sent as HTML/quoted-printable text, not base64, so it is not inflated.
const BASE64_OVERHEAD_FACTOR = 4 / 3;

// Fixed overhead added to every outgoing email regardless of attachments:
// the rendered HTML layout/signature ("emails.outbound" + signature partial,
// ~2.6 KB, sent as-is) plus the inline tenant logo image that
// OutboundMailLayoutDefinition::getInlineAttachments() attaches to every send
// (storage/images/logo.png, 27,297 raw bytes -> ~36.4 KB once base64-encoded).
// Measured manually, not backend-derived — a future iteration could expose
// this as a real config/API value instead.
const BASE_EMAIL_TEMPLATE_HTML_BYTES = 2556;
const BASE_EMAIL_LOGO_BYTES = 27297;
const BASE_EMAIL_OVERHEAD_BYTES =
  BASE_EMAIL_TEMPLATE_HTML_BYTES +
  Math.round(BASE_EMAIL_LOGO_BYTES * BASE64_OVERHEAD_FACTOR);

// Estimated total size of the outgoing email on the wire, given the RHF
// "anlagen" refs ({id, uuid, used_in}[]) and the full pool of files they're
// resolved against (existingFiles + sessionFiles). Shared by the inline
// size display and the over-limit confirmation check at submit time.
const estimateMailSize = (anlagenRefs, allFiles) => {
  const refs = Array.isArray(anlagenRefs)
    ? anlagenRefs
    : anlagenRefs
      ? [anlagenRefs]
      : [];

  const attachmentBytes = refs
    .map((ref) => allFiles.find((f) => f.id === ref?.id))
    .filter(Boolean)
    .reduce((sum, f) => sum + (f.mailOptimizedSize ?? f.size ?? 0), 0);

  return (
    BASE_EMAIL_OVERHEAD_BYTES +
    Math.round(attachmentBytes * BASE64_OVERHEAD_FACTOR)
  );
};

// The insurer-specific limit only applies when actually mailing the
// insurer; customer-directed mail is only ever measured against the
// global default.
const effectiveMailSizeLimit = (an_versicherer, mailSizeLimits) =>
  (an_versicherer ? mailSizeLimits?.insurer ?? mailSizeLimits?.default : mailSizeLimits?.default) ?? null;

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------
const validationSchema = yup.object({
  betreff: yup.string().required("Betreff ist erforderlich"),
  beschreibung: yup.string().required("Beschreibung ist erforderlich"),
  an_weitere: yup.array().of(yup.string().email()),
});

// ---------------------------------------------------------------------------
// Sub-component: Template fill buttons
// ---------------------------------------------------------------------------
const TemplateButtons = () => {
  const { setValue } = useFormContext();
  const an_versicherer = useWatch({ name: "an_versicherer" });
  const visible = FILL_TEMPLATES.filter(
    (t) =>
      t.visibleWhenAnVersicherer === null ||
      t.visibleWhenAnVersicherer === an_versicherer
  );

  if (!visible.length) return null;

  return (
    <Grid container spacing={1}>
      {visible.map((template, index) => (
        <Grid key={index} size={{ md: 4, lg: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              template.actions.forEach((action) => {
                setValue(action.target, action.value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              });
            }}
            sx={{
              fontSize: typographyTokens.size.sm,
              fontWeight: typographyTokens.weight.regular,
              textTransform: "none",
            }}
          >
            {template.label}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
};

const RecipientList = ({ recipients, insurerContacts = [] }) => {
  const an_versicherer = useWatch({ name: "an_versicherer" });
  const insurer_contact_id = useWatch({ name: "insurer_contact_id" });

  const selected_contact = insurerContacts.find(
    (contact) => contact.id === insurer_contact_id
  );

  const { enqueueSnackback } = useSnackbar();
  return (
    <>
      {an_versicherer && selected_contact?.email && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>An:</Box>
          <Box>
            <strong>{selected_contact.display_name}</strong>{" "}
            {`<${selected_contact.email.trim()}>`}
            <Typography
              variant="body2"
              sx={{ color: colorsFoundation.neutral[600] }}
            >
              Versicherer
            </Typography>
          </Box>
        </Box>
      )}

      {!an_versicherer && recipients?.claim_creator?.email && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>An:</Box>
          <Box>
            <strong>{recipients?.claim_creator.name}</strong>{" "}
            {`<${recipients.claim_creator.email.trim()}>`}{" "}
            <Typography
              variant="body2"
              sx={{ color: colorsFoundation.neutral[600] }}
            >
              Schadenersteller/Kunde - erhält eine Benachrichtigung über
              gesendete Mitteilung
            </Typography>
          </Box>
        </Box>
      )}

      {!an_versicherer && recipients?.claim_contact?.email && (
        <Alert severity="info">
          <Typography>Abweichende Kontaktperson</Typography>
          <Typography variant="body2">
            Im Schadenformular ist folgender Kontakt hinterlegt. Um eine E-Mail
            an diesen Kontakt zu senden, tragen Sie bitte die E-Mailadresse in
            das Feld "Weitere Empfänger" ein.
          </Typography>
          <br />
          <strong>{recipients?.claim_contact.name}</strong>{" "}
          {`<${recipients.claim_contact.email.trim()}>`}{" "}
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            onClick={() => {
              navigator.clipboard.writeText(recipients.claim_contact.email);
              enqueueSnackbar("E-Mail kopiert", { variant: "success" });
            }}
          >
            <CopyIcon
              sx={{
                fontSize: "1rem",
                mr: 1,
                color: colorsFoundation.key.blue[700],
              }}
            />
          </IconButton>
          <Typography variant="body2">
            Ansprechpartner aus Schadenformular/Kunde
          </Typography>
        </Alert>
      )}

      {recipients?.assigned_clerk?.name && (
        <Box sx={{ mb: 1 }}>
          An: <strong>{recipients?.assigned_clerk.name}</strong>{" "}
          {`<${recipients.assigned_clerk.email}>`}
          <Typography>Ihr Sachbearbeiter</Typography>
        </Box>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Choice of the insurer recipient among the insurer's
// contacts. A new message starts with the contact last used for this claim
// (else the default contact); a draft keeps its own.
// ---------------------------------------------------------------------------
const InsurerContactSelect = ({
  contacts,
  canCreate,
  onCreateContact,
  disabled,
}) => {
  const { control, setValue } = useFormContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCreate = async (values) => {
    const contact = await onCreateContact(values);
    setValue("insurer_contact_id", contact.id, { shouldDirty: true });
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Controller
        name="insurer_contact_id"
        control={control}
        render={({ field }) => {
          const value = field.value ?? "";
          const is_unavailable =
            value !== "" && !contacts.some((contact) => contact.id === value);

          return (
            <TextField
              select
              fullWidth
              label="Empfänger Versicherer"
              value={value}
              disabled={disabled}
              onChange={(e) => field.onChange(e.target.value)}
            >
              {contacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.id}>
                  {`${contact.display_name} <${contact.email.trim()}>`}
                </MenuItem>
              ))}
              {is_unavailable && (
                <MenuItem value={value} disabled>
                  Kontakt nicht mehr verfügbar
                </MenuItem>
              )}
            </TextField>
          );
        }}
      />
      {canCreate && !disabled && (
        <DsButton
          variant="secondary"
          onClick={() => setDialogOpen(true)}
          sx={{ whiteSpace: "nowrap", mt: 0.5 }}
        >
          Neuer Kontakt
        </DsButton>
      )}
      <NewInsurerContactDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreate={handleCreate}
      />
    </Box>
  );
};

const InsurerContactSelectField = ({
  kommunikationId,
  preselectedId,
  ...props
}) => {
  const { setValue, getValues } = useFormContext();
  const an_versicherer = useWatch({ name: "an_versicherer" });

  // A new message takes the last used contact (else the default contact).
  useEffect(() => {
    if (!kommunikationId && preselectedId != null) {
      setValue("insurer_contact_id", preselectedId);
    }
  }, [kommunikationId, preselectedId, setValue]);

  // A draft keeps its own contact; one saved without a contact means the
  // default contact.
  const default_contact_id = props.contacts.find(
    (contact) => contact.is_default
  )?.id;
  useEffect(() => {
    if (
      kommunikationId &&
      default_contact_id != null &&
      getValues("insurer_contact_id") == null
    ) {
      setValue("insurer_contact_id", default_contact_id);
    }
  }, [kommunikationId, default_contact_id, setValue, getValues]);

  if (!an_versicherer) return null;

  return <InsurerContactSelect {...props} />;
};

const Recipient = ({ disabled }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name="an_versicherer"
      control={control}
      render={({ field }) => {
        return (
          <ToggleButtonGroup
            exclusive
            disabled={disabled}
            sx={{
              borderRadius: 8,
              overflow: "hidden",

              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: 0,
                border: 0,
              },

              "& .MuiToggleButtonGroup-grouped:first-of-type": {
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              },
              "& .MuiToggleButtonGroup-grouped:last-of-type": {
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
              },

              "& .MuiToggleButton-root": {
                backgroundColor: colorsFoundation.neutral[50],
                color: colorsFoundation.neutral[800],
                borderWidth: 1,
                borderColor: colorsFoundation.neutral[300],
              },

              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: colorsFoundation.key.blue[700],
                color: "primary.contrastText",
                borderColor: colorsFoundation.key.blue[700],
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                backgroundColor: colorsFoundation.key.blue[800],
              },
            }}
            value={field.value}
            onChange={(e, value) => {
              if (value !== null) field.onChange(value);
            }}
          >
            <ToggleButton value={false} color="primary">
              Kunde
            </ToggleButton>
            <ToggleButton value={true} color="primary">
              Versicherer
            </ToggleButton>
          </ToggleButtonGroup>
        );
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Total size of the outgoing email on the wire (see
// estimateMailSize), color-coded against the applicable size limit — the
// per-insurer override when mailing the insurer and one is set, otherwise
// the global default (mailSizeLimits, from GET preview-recipients).
// ---------------------------------------------------------------------------
const MessageSize = ({ existingFiles, sessionFiles, mailSizeLimits }) => {
  const anlagen = useWatch({ name: "anlagen" }) ?? [];
  const an_versicherer = useWatch({ name: "an_versicherer" });
  const allFiles = [...existingFiles, ...sessionFiles];

  const totalBytes = estimateMailSize(anlagen, allFiles);
  const limitBytes = effectiveMailSizeLimit(an_versicherer, mailSizeLimits);

  let color = "text.secondary";
  if (limitBytes) {
    if (totalBytes >= limitBytes) color = "error.main";
    else if (totalBytes >= limitBytes * 0.9) color = "warning.main";
  }

  return (
    <Typography variant="body2" sx={{ color }}>
      Voraussichtliche Größe der E-Mail: {formatFileSize(totalBytes)}
    </Typography>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Confirmation shown when the estimated email size exceeds
// the applicable limit. Sending is never blocked — this only asks the
// clerk to confirm they want to proceed anyway.
// ---------------------------------------------------------------------------
const MailSizeWarningDialog = ({ open, totalBytes, limitBytes, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>Mitteilung möglicherweise zu groß</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Die voraussichtliche Größe dieser E-Mail ({formatFileSize(totalBytes)}
        ) überschreitet das Größenlimit von {formatFileSize(limitBytes)}. Der
        Mailserver des Empfängers wird die E-Mail dadurch möglicherweise
        nicht annehmen. Trotzdem senden?
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ padding: "16px" }}>
      <DsButton variant="tertiary" size="medium" onClick={onCancel}>
        Abbrechen
      </DsButton>
      <DsButton variant="primary" size="medium" color="warning" onClick={onConfirm}>
        Trotzdem senden
      </DsButton>
    </DialogActions>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Sub-component: Quick date buttons for "faellig_am"
// ---------------------------------------------------------------------------
const QuickDateButtons = ({ disabled }) => {
  const { setValue } = useFormContext();
  const faellig_am = useWatch({ name: "faellig_am" });

  if (disabled) return null;

  const handleClick = (days) => {
    if (days === null) {
      setValue("faellig_am", null);
      return;
    }

    setValue("faellig_am", getDateInFuture(days), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        height: "100%",
        alignItems: "center",
      }}
    >
      {QUICK_DATES.map((option) => {
        const isSelected =
          faellig_am &&
          getDateInFuture(option.days).toDateString() ===
            new Date(faellig_am).toDateString();

        return (
          <DsButton
            key={option.days}
            size="small"
            variant={isSelected ? "secondary" : "tertiary"}
            onClick={() => handleClick(option.days)}
          >
            {option.label}
          </DsButton>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Save Draft button (bypasses validation)
// ---------------------------------------------------------------------------
const SaveDraftButton = ({ onSaveDraft }) => {
  const { getValues } = useFormContext();
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    setSubmitting(true);
    try {
      await onSaveDraft(getValues());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DsButton
      type="button"
      variant="secondary"
      onClick={handleClick}
      disabled={submitting}
    >
      {submitting ? "Wird gespeichert..." : "Entwurf speichern"}
    </DsButton>
  );
};

SaveDraftButton.propTypes = {
  onSaveDraft: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Main form component
// ---------------------------------------------------------------------------
const CommunicationEmailForm = ({
  defaultValues,
  onSubmit,
  onSaveDraft,
  onCancel,
  canMessageInsurer,
  schadenId,
  kommunikationId,
  disabled,
  submitLabel,
  submitRequireDirty = true,
}) => {
  const { files: existingFiles } = useClaimAttachmentFiles({
    claimId: schadenId,
  });

  const { sessionFiles, addSessionFile } = useSessionFiles();

  const uploadFn = useCallback(
    (file, uuid, description) =>
      uploadClaimAttachment(schadenId, file, uuid, description),
    [schadenId]
  );

  const handleAddSessionFile = useCallback(
    (file) => addSessionFile(file, uploadFn),
    [addSessionFile, uploadFn]
  );

  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mailSizeLimits, setMailSizeLimits] = useState(null);
  const [insurerContacts, setInsurerContacts] = useState([]);
  const [preselectedContactId, setPreselectedContactId] = useState(null);
  const [canCreateInsurerContact, setCanCreateInsurerContact] = useState(false);
  const [oversizeConfirm, setOversizeConfirm] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      await backendGet(`data/kommunikations/${schadenId}/preview-recipients`, {
        setResult: (res) => {
          setPreviewRecipients(res?.data?.recipients || []);
          setMailSizeLimits(res?.data?.mail_size_limits ?? null);
          setInsurerContacts(res?.data?.insurer_contacts ?? []);
          setPreselectedContactId(
            res?.data?.preselected_insurer_contact_id ?? null
          );
          setCanCreateInsurerContact(
            !!res?.data?.can_create_insurer_contact
          );
        },
        setIsLoading: setPreviewLoading,
      });
    };

    fetchPreview();
  }, [schadenId]);

  // Rejects on failure so the dialog can show a validation error on its e-mail field.
  const createInsurerContact = async (values) => {
    const contact = (await createInsurerContactFromClaim(schadenId, values)).data;
    setInsurerContacts((contacts) => [...contacts, contact]);
    return contact;
  };

  const handleSubmit = async (data) => {
    const allFiles = [...existingFiles, ...sessionFiles];
    const totalBytes = estimateMailSize(data.anlagen, allFiles);
    const limitBytes = effectiveMailSizeLimit(
      data.an_versicherer,
      mailSizeLimits
    );

    if (limitBytes && totalBytes >= limitBytes) {
      setOversizeConfirm({ data, totalBytes, limitBytes });
      return;
    }

    await onSubmit(data);
  };

  const confirmOversizeSend = async () => {
    const pending = oversizeConfirm;
    setOversizeConfirm(null);
    if (pending) await onSubmit(pending.data);
  };

  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      resolver={yupResolver(validationSchema)}
      watchFields={["an_versicherer"]}
      context={{ canMessageInsurer }}
      mode="onChange"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {/* fields for users that have the ability to write to the insurance company */}
        {canMessageInsurer ? (
          <>
            <Recipient disabled={!!kommunikationId} />

            <RecipientList
              recipients={previewRecipients}
              insurerContacts={insurerContacts}
            />

            <InsurerContactSelectField
              kommunikationId={kommunikationId}
              contacts={insurerContacts}
              preselectedId={preselectedContactId}
              canCreate={canCreateInsurerContact}
              onCreateContact={createInsurerContact}
              disabled={disabled}
            />

            <FormEmailInput
              name="an_weitere"
              label="Weitere E-Mail-Empfänger"
              helperText="Geben Sie ggfs. weitere E-Mailadressen ein, die diese Mitteilung erhalten sollen"
              disabled={disabled}
            />
            {/* Template buttons (only in edit mode) */}
            {!disabled && <TemplateButtons />}
          </>
        ) : (
          <RecipientList recipients={previewRecipients} />
        )}

        {/* Subject */}
        <FormTextField
          name="betreff"
          label="Betreff"
          fullWidth
          disabled={disabled}
        />

        {/* Body */}
        <FormTextField
          name="beschreibung"
          label="Beschreibung"
          fullWidth
          multiline
          rows={12}
          disabled={disabled}
        />

        {/* Attachments */}
        <FormFilePickerField
          name="anlagen"
          label="Anlagen"
          multiple
          existingFiles={existingFiles}
          sessionFiles={sessionFiles}
          onAddSessionFile={handleAddSessionFile}
          showUsedInFilter={true}
          disabled={disabled}
        />

        {/* Reminder Due Date */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormDatePicker
              name="faellig_am"
              label="Erinnern am"
              disabled={disabled}
              clearable={true}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <QuickDateButtons disabled={disabled} />
          </Grid>
        </Grid>

        {/* Info text */}
        {canMessageInsurer && (
          <Typography variant="body2" color="text.secondary">
            Hinweis: die Platzhalter werden beim Speichern automatisch ersetzt.
            Beim Versand per E-Mail wird außerdem automatisch eine E-Mail
            Signatur hinzugefügt.
          </Typography>
        )}

        <Box sx={{ textAlign: "right" }}>
          <MessageSize
            existingFiles={existingFiles}
            sessionFiles={sessionFiles}
            mailSizeLimits={mailSizeLimits}
          />
        </Box>

        <MailSizeWarningDialog
          open={!!oversizeConfirm}
          totalBytes={oversizeConfirm?.totalBytes}
          limitBytes={oversizeConfirm?.limitBytes}
          onConfirm={confirmOversizeSend}
          onCancel={() => setOversizeConfirm(null)}
        />

        {/* Action buttons */}
        {!disabled && (
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}
          >
            <DsButton variant="tertiary" onClick={onCancel}>
              Abbrechen
            </DsButton>
            {onSaveDraft && <SaveDraftButton onSaveDraft={onSaveDraft} />}
            <SubmitButton requireDirty={submitRequireDirty}>
              {submitLabel || "Speichern"}
            </SubmitButton>
          </Box>
        )}

        {disabled && (
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}
          >
            <Button variant="text" onClick={onCancel}>
              Schließen
            </Button>
          </Box>
        )}
      </Box>
    </Form>
  );
};

CommunicationEmailForm.propTypes = {
  defaultValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSaveDraft: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  canMessageInsurer: PropTypes.bool,
  schadenId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kommunikationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  submitLabel: PropTypes.string,
  submitRequireDirty: PropTypes.bool,
};

export default CommunicationEmailForm;
